import React, { useState, useEffect } from "react";
import Footer from "../components/Footer";
import { CheckCircle2, XCircle, Shield, Search, Sparkles } from "lucide-react";
import api from "../config/api";

const VerifyCertificate = () => {
  const [certificateId, setCertificateId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let interval;
    if (waiting) {
      setProgress(0);
      let start = Date.now();
      const duration = 2500; // Refined 2.5s simulated database validation
      interval = setInterval(() => {
        const elapsed = Date.now() - start;
        let percent = Math.min(100, Math.round((elapsed / duration) * 100));
        setProgress(percent);
        if (percent >= 100) {
          clearInterval(interval);
        }
      }, 50);
    } else {
      setProgress(0);
      if (interval) clearInterval(interval);
    }
    return () => interval && clearInterval(interval);
  }, [waiting]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!certificateId.trim()) return;

    setResult(null);
    setError("");
    setShowModal(false);
    setWaiting(true);
    setLoading(true);

    let apiResult = null;
    let apiError = "";

    try {
      const res = await api.get(`/certificates/verify/${certificateId.trim()}`);
      if (res.data.success && res.data.valid) {
        apiResult = res.data.data;
      } else {
        apiError = res.data.message || "Certificate not found.";
      }
    } catch (err) {
      apiError = "Details do not match. Please verify the Certificate ID and try again.";
    }

    setLoading(false);

    // Complete the visual scan and then show the modal
    setTimeout(() => {
      if (apiError) {
        setError(apiError);
      } else {
        setResult(apiResult);
      }
      setShowModal(true);
      setWaiting(false);
    }, 2500);
  };

  return (
    <div className="min-h-screen flex flex-col font-sans bg-slate-50 text-slate-800">
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-16 sm:py-20">
        <div className="bg-white p-6 sm:p-10 md:p-12 rounded-3xl border border-slate-200 w-full max-w-xl shadow-sm">
          
          <div className="flex flex-col items-center mb-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center border border-indigo-100 mb-5">
              <Shield className="w-8 h-8" />
            </div>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-bold text-[10px] border border-emerald-100 uppercase tracking-wider mb-3">
              <Sparkles className="w-3 h-3 text-emerald-600" /> SECURE CRYTOGRAPHIC VERIFICATION
            </span>
            <h1 className="text-3xl font-extrabold text-slate-900 mb-3 tracking-tight">
              Verify Certificate
            </h1>
            <p className="text-slate-500 text-sm max-w-md">
              Validate the authenticity of any Student Era credential by entering the unique Certificate ID.
            </p>
          </div>

          <form onSubmit={handleVerify} className="flex flex-col gap-5">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Enter Certificate ID (e.g. SE-12345)"
                value={certificateId}
                onChange={(e) => setCertificateId(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-2xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-slate-900 bg-slate-50 shadow-sm transition-all"
                required
                autoFocus
              />
            </div>
            <button
              type="submit"
              className="px-6 py-4 bg-indigo-600 text-white rounded-2xl shadow-md font-bold hover:bg-indigo-700 hover-glow transition-all flex items-center justify-center gap-2 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-base"
              disabled={loading || waiting || !certificateId.trim()}
            >
              {loading || waiting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Querying database...
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" /> Validate Credential
                </>
              )}
            </button>
          </form>

          {waiting && (
            <div className="mt-8 flex flex-col items-center justify-center animate-pulse">
              <div className="w-full max-w-xs">
                <div className="flex justify-between text-[10px] font-bold text-slate-500 mb-2 uppercase tracking-wider">
                  <span>Cryptographic Check</span>
                  <span className="text-indigo-600">{progress}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-600 rounded-full transition-all duration-150 ease-out"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Modal */}
        {showModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-slate-900/60 backdrop-blur-sm px-4">
            <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-10 max-w-md w-full relative border border-slate-200 max-h-[90vh] overflow-y-auto">
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors p-1"
                aria-label="Close"
              >
                <XCircle className="w-6 h-6" />
              </button>

              {error ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-rose-50 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-5 border border-rose-100">
                    <XCircle className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    Verification Failed
                  </h3>
                  <p className="text-slate-500 text-sm mb-6 leading-relaxed">{error}</p>
                  <button
                    onClick={() => setShowModal(false)}
                    className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-colors text-sm"
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-5 border border-emerald-100">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-1">
                    Credential Verified
                  </h3>
                  <p className="text-emerald-600 font-bold text-xs uppercase tracking-wider mb-6">Official & Authentic Record</p>

                  <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-5 text-left space-y-3 mb-6">
                    <div className="flex justify-between border-b border-slate-200/60 pb-2">
                      <span className="text-slate-400 text-xs font-semibold">Issued To</span>
                      <span className="font-bold text-slate-900 text-xs sm:text-sm text-right">{result.name}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-200/60 pb-2">
                      <span className="text-slate-400 text-xs font-semibold">Program</span>
                      <span className="font-bold text-indigo-700 text-xs sm:text-sm text-right ml-4 line-clamp-1">{result.internshipTitle}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-200/60 pb-2">
                      <span className="text-slate-400 text-xs font-semibold">Duration</span>
                      <span className="font-semibold text-slate-800 text-xs sm:text-sm text-right">{result.duration}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-200/60 pb-2">
                      <span className="text-slate-400 text-xs font-semibold">Date Completed</span>
                      <span className="font-semibold text-slate-800 text-xs sm:text-sm text-right">
                        {result.completionDate ? new Date(result.completionDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : "-"}
                      </span>
                    </div>
                    <div className="flex justify-between pt-1 items-center">
                      <span className="text-slate-400 text-xs font-semibold">Certificate ID</span>
                      <span className="font-mono font-bold text-indigo-600 text-xs bg-indigo-50 px-2.5 py-1 rounded-lg border border-indigo-100">{result.certificateId}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowModal(false)}
                    className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 hover-glow text-white font-bold rounded-xl transition-all text-sm"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default VerifyCertificate;
